import { motion } from "framer-motion";
import { ReactNode, useEffect, useState } from "react";
import { useMaxView, useViewport } from "src/contexts";
import {
  IconButton,
  SkeletonAccordionOptionCard,
  SkeletonProductAddOnLoaderCard,
  SkeletonSelectableOptionCard,
  Tag,
  TagProps,
} from "~components";
import { Css } from "~generated/css";
import { useLocalStorage, useTestIds } from "~utils";
import { useConfiguratorContext } from "../Configurator.context";
import { FlowStep } from "../stepTemplates";
import { HeaderHeight, MobileHeaderId } from "./ConfigurationHeader";
import { OptionListContainer } from "./options";
import { FooterHeight } from "./stepper/stepper";

type StepWrapperProps = {
  /** Component to rendered in the left column / top row in mobile */
  left: ReactNode;
  /** Right column tag. Unused in mobile */
  tag?: TagProps;
  /** Right column title under tag. Unused in mobile */
  title: ReactNode;
  /** Right column description under title. Renders in top most row in mobile */
  description: string | ReactNode;
  /** Component to rendered in the right column under description. Renders in bottom row in mobile */
  right: ReactNode;
  /** Component to rendered under the right column. */
  rightFooter?: ReactNode;
  hideBorder?: boolean;
  // Use if we're not showing ImageDisplay or ConfiguratorMap in the left column (top row) on mobile
  showMaxExpand?: boolean;
  /** Renders Loading spinner over right column. Blocks pointer events. */
  loading?: boolean;
  /** Loading view to render when loading is true. Defaults to selectable. */
  loadingView?: "personalization" | "selectable" | "accordion";
  inSummaryDrawer?: boolean;
  /** Renders below description in desktop. Below {left} in mobile */
  warningNode?: ReactNode;
  /** mobile only: Hides the left (top) content. */
  hideTopOnMobile?: boolean;
};

const MAP_PINNED_KEY = "configurator_viewPinned";

const borderedRightContainer = Css.oh.ba.bcGray16.br4.addIn("> div:not(:last-child)", Css.br0.bb.bcGray16.$).$;

export function StepWrapper(props: StepWrapperProps) {
  const { isMobile } = useViewport();
  return isMobile ? <MobileStepWrapper {...props} /> : <DesktopStepWrapper {...props} />;
}

// Keeping it separate from ConfiguratorStep so children animate in
export function DesktopStepWrapper(props: StepWrapperProps) {
  const tid = useTestIds(props, "step-wrapper");
  const {
    loading = false,
    loadingView = "selectable",
    left,
    right,
    rightFooter,
    description,
    title,
    tag,
    hideBorder = false,
    warningNode,
  } = props;
  return (
    <div
      css={
        Css.bgWhite.fg1.oh.asc
          .h(`calc(100vh - ${HeaderHeight + FooterHeight}px)`)
          .w100.dg.gtc("repeat(16, 1fr)")
          .boxShadow("-4px -4px 16px rgba(201, 201, 201, 0.2), 4px 4px 32px rgba(228, 228, 231, 0.2)").$
      }
    >
      {/* LEFT */}
      <div css={Css.oh.relative.z0.df.fdc.bgLightBlue.ifXlAndDown.gc("1 / 12").else.gc("1 / 13").$}>{left}</div>
      {/* RIGHT */}
      <motion.div
        layoutScroll
        id="right-container"
        css={Css.oa.relative.z1.df.fdc.p3.ifXlAndDown.gc("12 / 17").else.gc("13 / 17").$}
      >
        <motion.div layout="position" css={Css.df.fdc.gap4.w100.$}>
          <div>
            {tag && (
              <div>
                <Tag key={tag.children} {...tag} />
              </div>
            )}
            <div css={Css.t32.gray800.mb2.$} {...tid.title}>
              {title}
            </div>
            <div css={Css.df.fdc.t14.lh("1.25em").gray.if(!!warningNode).gap1.$} {...tid.description}>
              {description}
              {warningNode && warningNode}
            </div>
          </div>
          <motion.div
            layout="position"
            css={{
              ...Css.df.fdc.$,
              ...(!hideBorder && borderedRightContainer),
              // Loading state will block pointer events while selections are being saved
              ...(loading && Css.pointerNone.$),
            }}
          >
            {!loading && right}
            {loading && <LoadingSkeletons loadingView={loadingView} />}
          </motion.div>
          {rightFooter}
        </motion.div>
      </motion.div>
    </div>
  );
}

export function MobileStepWrapper(props: StepWrapperProps) {
  const tid = useTestIds(props, "step-wrapper");
  const {
    loading = false,
    loadingView = "selectable",
    left,
    right,
    rightFooter,
    description,
    hideBorder = false,
    showMaxExpand = false,
    title,
    inSummaryDrawer = false,
    warningNode,
    hideTopOnMobile = false,
  } = props;
  const { checkoutConfig, currentFlowStep } = useConfiguratorContext();
  const [isPinned, setIsPinned] = useLocalStorage(MAP_PINNED_KEY, true);
  const { openMaxViewModal } = useMaxView();
  const [mobileHeaderHeight, setMobileHeaderHeight] = useState(0);

  // Used to add extra padding to container
  const showingReceiptSummary =
    currentFlowStep !== FlowStep.Summary && !!checkoutConfig?.productOffering && !inSummaryDrawer;

  useEffect(() => {
    const header = document.getElementById(MobileHeaderId);
    // Stick to the top of the screen when we're in the summary drawer
    if (header && !inSummaryDrawer) {
      setMobileHeaderHeight(header.offsetHeight);
    }
    // Our header might increase in size after the configuration has been loaded and we start showing
    // an interior sub-step with a longer title that wraps. Our steps feed a title regardless of device so watch that.
  }, [inSummaryDrawer, title]);

  // Set top scroll position to top when the step changes
  // NOTE: we're setting top: 1 to avoid the address bar re-expanding which would push up our nav buttons and could be annoying
  useEffect(() => {
    window.scrollTo({ top: 1, behavior: "instant" });
  }, [title]);

  return (
    <>
      {description && (
        <div css={Css.pt1.pb2.px2.t14.lh("1.25em").gray.$} {...tid.description}>
          {description}
        </div>
      )}
      {/* Top */}
      {!hideTopOnMobile && (
        <div
          {...tid.top_container}
          css={Css.oh.relative.z1.df.fdc.mh("50vh").vh50.w100.if(isPinned).sticky.topPx(mobileHeaderHeight).$}
        >
          <div css={Css.absolute.top2.right2.z2.df.gap2.$}>
            {/* [SC-81423] Expanding inside maps renders a 2nd iframe which is crashing on mobile.
            Disabling for now while we work on a real fix. */}
            {/* {showMaxExpand && (
              <IconButton
                icon="expand-small"
                color="primary"
                size="medium"
                shape="square"
                onClick={() => openMaxViewModal(left)}
                {...tid.expand_button}
              />
            )} */}
            <IconButton
              icon={isPinned ? "pin-white" : "pin-black"}
              color={isPinned ? "secondary" : "tertiary"}
              size="medium"
              shape="square"
              onClick={() => setIsPinned(!isPinned)}
              {...tid.pin_button}
            />
          </div>
          {left}
        </div>
      )}
      {warningNode && (
        <div css={Css.pt1.pb2.px2.t14.$} {...tid.warningNode}>
          {warningNode}
        </div>
      )}
      {/* Bottom */}
      <motion.div
        layoutScroll
        {...tid.bottom_container}
        // Account for both bottom footers with some padding at the end of bottom content;
        css={
          Css.relative.z0.df.fdc.px2.pt2.pbPx(FooterHeight * (showingReceiptSummary ? 2 : 1) + 16).if(hideTopOnMobile)
            .pt0.$
        }
      >
        <motion.div layout="position" css={Css.df.fdc.gap4.w100.$}>
          <motion.div
            layout="position"
            css={{
              ...Css.df.fdc.$,
              ...(!hideBorder && borderedRightContainer),
              // Loading state will block pointer events while selections are being saved
              ...(loading && Css.pointerNone.$),
            }}
          >
            {!loading && right}
            {loading && <LoadingSkeletons loadingView={loadingView} />}
          </motion.div>
          {rightFooter}
        </motion.div>
      </motion.div>
    </>
  );
}

/**
 * Scrolls to an element in the StepWrapper, accounting for sticky map when pin functionality is used
 * @param elId - The element ID to scroll to
 */
export function scrollToStepWrapperElement(elId: string, isMobile: boolean) {
  const element = document.getElementById(elId);
  if (!element) return;

  // Check if map is pinned
  const isPinned = JSON.parse(localStorage.getItem(MAP_PINNED_KEY) || "true");

  if (isPinned && isMobile) {
    // Account for the ~50vh sticky map when pinned
    const offset = window.innerHeight * 0.63;
    const elementPosition = element.getBoundingClientRect().top + window.pageYOffset;
    window.scrollTo({ top: elementPosition - offset, behavior: "smooth" });
  } else {
    // Normal scroll behavior when not pinned
    element.scrollIntoView({ behavior: "smooth", block: "center" });
  }
}

function LoadingSkeletons({ loadingView }: { loadingView: StepWrapperProps["loadingView"] }) {
  switch (loadingView) {
    case "personalization":
      return (
        <OptionListContainer>
          <SkeletonProductAddOnLoaderCard />
          <SkeletonProductAddOnLoaderCard />
          <SkeletonProductAddOnLoaderCard />
        </OptionListContainer>
      );
    case "selectable":
      return (
        <OptionListContainer>
          <SkeletonSelectableOptionCard />
          <SkeletonSelectableOptionCard />
        </OptionListContainer>
      );
    case "accordion":
    default:
      return (
        <>
          <SkeletonAccordionOptionCard />
          <SkeletonAccordionOptionCard />
        </>
      );
  }
}
